import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { AssistantTurnError, safePath, streamAssistantTurn } from '../../services/assistant';
import type { AssistantAction, AssistantChoice, AssistantDraft } from '../../types/assistant';
import { useFeatureFlags } from '../feature-flags/useFeatureFlags';
import { getAssistantAction, type AssistantActionContext } from './actionRegistry';
import {
  AssistantContext,
  type AssistantContextValue,
  type AssistantMessage,
  type AssistantAttachment,
  type AssistantPrimary,
  type AssistantSendOutcome,
  type OpenDraft,
  type ScreenIds,
  type ShownView,
} from './assistantContext';
import { ASSISTANT_DOCK_QUERY } from './assistantLayout';
import { buildTurnRequest, landReply, screenContextOf } from './assistantState';
import { getDraftHandler, readAssistantDraft } from './draftRegistry';

/** Messages kept for the visit; older ones (and the views only they brought) go, so a long session stays small. */
export const MAX_KEPT_MESSAGES = 60;

const keepNewest = (messages: AssistantMessage[]): AssistantMessage[] =>
  messages.length > MAX_KEPT_MESSAGES ? messages.slice(-MAX_KEPT_MESSAGES) : messages;

let nextId = 0;
const newId = () => `assistant-${(nextId += 1)}`;

const userMessage = (text: string, attachments: string[] = []): AssistantMessage => ({
  id: newId(),
  role: 'user',
  text,
  ...(attachments.length > 0 ? { attachments } : {}),
  viewKeys: [],
  draftKeys: [],
  suggestions: [],
  question: null,
});

const noteMessage = (text: string, failed = false, openPath: string | null = null, openLabel: string | null = null): AssistantMessage => ({
  id: newId(),
  role: 'assistant',
  text,
  failed,
  local: !failed,
  openPath,
  openLabel,
  viewKeys: [],
  draftKeys: [],
  suggestions: [],
  question: null,
});

/**
 * The app-wide assistant's state for the signed-in shell: the conversation (kept while the coach
 * moves between pages), the views and drafts it brought, what the panel shows, and the pages' screen
 * ids. Renders its children unchanged; the panel and header button read it through `useAssistant`.
 * With the `ai.assistant` flag off every call is inert.
 */
export const AssistantProvider = ({ children }: { children: ReactNode }) => {
  const flagOn = useFeatureFlags().isEnabled('ai.assistant');
  const navigate = useNavigate();
  const location = useLocation();
  const { user, activeOrganization, activeView } = useAuth();
  // College only: every tool reads a college program's data, and a travel ball view has none.
  const enabled = flagOn && !!user?.profile && (activeView === null || activeView.kind === 'college');
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<AssistantMessage[]>([]);
  const [views, setViews] = useState<ShownView[]>([]);
  const [drafts, setDrafts] = useState<OpenDraft[]>([]);
  const [primary, setPrimary] = useState<AssistantPrimary>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [openFocus, setOpenFocus] = useState(true);
  // Goes up on every switch of account or organization: a confirm that started before it lands in nobody's thread.
  const scopeEpoch = useRef(0);
  const controller = useRef<AbortController | null>(null);
  const generation = useRef(0);
  const turn = useRef(0);
  const screenIds = useRef(new Map<string, ScreenIds>());
  const mounted = useRef(true);
  // The latest state for callbacks that must not change identity on every message.
  const latest = useRef({ messages, views, drafts, primary, route: location.pathname });
  latest.current = { messages, views, drafts, primary, route: location.pathname };

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      controller.current?.abort();
    };
  }, []);

  const reset = useCallback((scopeChanged = false) => {
    generation.current += 1;
    controller.current?.abort();
    controller.current = null;
    setMessages([]);
    setViews([]);
    // New conversation: a draft mid-save finishes and stays. Another account or organization: nothing stays, and the
    // save's answer is dropped when it lands (the epoch moved on).
    if (scopeChanged) scopeEpoch.current += 1;
    setDrafts((current) => (scopeChanged ? [] : current.filter((d) => d.saving)));
    setPrimary(null);
    setStatus(null);
    setNotice(null);
  }, []);

  // A conversation belongs to one account and one organization: a switch starts over.
  const scope = `${user?.uid ?? ''}:${activeOrganization?.id ?? user?.profile?.organizationId ?? ''}`;
  const lastScope = useRef(scope);
  useEffect(() => {
    if (lastScope.current === scope) return;
    lastScope.current = scope;
    reset(true);
    setIsOpen(false);
  }, [scope, reset]);

  // Below the dock width the panel covers the page, so a navigation (Open, Show all, a player's name) would happen out
  // of sight behind it: the panel steps aside instead. Docked, the page beside it is the one that changes.
  const where = `${location.pathname}${location.search}`;
  const lastWhere = useRef(where);
  useEffect(() => {
    if (lastWhere.current === where) return;
    lastWhere.current = where;
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function' && !window.matchMedia(ASSISTANT_DOCK_QUERY).matches) {
      setIsOpen(false);
    }
  }, [where]);

  const send = useCallback(
    (raw: string, choice?: AssistantChoice, attachments: readonly AssistantAttachment[] = []): Promise<AssistantSendOutcome> => {
      const text = raw.trim();
      if ((!text && attachments.length === 0) || !enabled) return Promise.resolve('ignored');
      controller.current?.abort();
      const abort = new AbortController();
      controller.current = abort;
      generation.current += 1;
      const mine = generation.current;
      turn.current += 1;
      const turnNumber = turn.current;

      const { messages: before, views: shownViews, drafts: openDrafts, primary: shown, route } = latest.current;
      const primaryView = shown?.type === 'view' ? (shownViews.find((v) => v.key === shown.key) ?? null) : null;
      const context = screenContextOf(route, primaryView, [...screenIds.current.values()], choice);
      const request = buildTurnRequest(text, before, context, openDrafts, new Date(), attachments);

      setMessages((current) => keepNewest([...current, userMessage(text, attachments.map((a) => a.name))]));
      setStatus(attachments.length > 0 ? 'Reading your files' : 'Reading your request');
      setNotice(null);
      return streamAssistantTurn(
        request,
        (line) => {
          if (generation.current === mine && mounted.current) setStatus(line);
        },
        abort.signal
      )
        .then((reply): AssistantSendOutcome => {
          if (generation.current !== mine || !mounted.current) return 'stopped';
          const id = newId();
          const landed = landReply(reply, turnNumber, id, []);
          setMessages((current) => keepNewest([...current, landed.message]));
          // The views only dropped messages brought go with them (the one on screen stays).
          const keys = new Set(keepNewest([...latest.current.messages, landed.message]).flatMap((m) => m.viewKeys));
          const shown = landed.changesPrimary ? landed.primary : latest.current.primary;
          if (shown?.type === 'view') keys.add(shown.key);
          setViews((views) => [...views, ...landed.views].filter((v) => keys.has(v.key)));
          // Merged over the current drafts, not a snapshot: a confirm or an edit queued meanwhile is kept.
          setDrafts((current) => landReply(reply, turnNumber, id, current).drafts);
          if (landed.changesPrimary) setPrimary(landed.primary);
          setNotice(reply.message);
          return 'replied';
        })
        .catch((err: unknown): AssistantSendOutcome => {
          if (generation.current !== mine || !mounted.current) return 'stopped';
          if (err instanceof DOMException && err.name === 'AbortError') return 'stopped';
          const message = err instanceof AssistantTurnError ? err.message : 'The assistant could not answer just now. Try again in a moment.';
          setMessages((current) => keepNewest([...current, noteMessage(message, true)]));
          setNotice(message);
          return err instanceof AssistantTurnError && err.refused ? 'refused' : 'failed';
        })
        .finally(() => {
          if (generation.current === mine && mounted.current) setStatus(null);
          if (controller.current === abort) controller.current = null;
        });
    },
    [enabled]
  );

  const stop = useCallback(() => {
    generation.current += 1;
    controller.current?.abort();
    controller.current = null;
    setStatus(null);
  }, []);

  const openDraft = useCallback((draft: AssistantDraft) => {
    turn.current += 1;
    const key = `local${turn.current}-${draft.id || 'd1'}`;
    setDrafts((current) => [...current.filter((d) => d.saving || d.draft.kind !== draft.kind), { key, draft, saving: false, error: null }]);
    setPrimary({ type: 'draft', key });
    setIsOpen(true);
  }, []);

  const updateDraft = useCallback((key: string, payload: unknown) => {
    setDrafts((current) =>
      current.map((d) => (d.key === key && !d.saving ? { ...d, draft: { ...d.draft, payload }, error: null } : d))
    );
  }, []);

  const discardDraft = useCallback((key: string) => {
    setDrafts((current) => current.filter((d) => d.key !== key || d.saving));
    setPrimary((current) => (current?.type === 'draft' && current.key === key ? null : current));
  }, []);

  const confirmDraft = useCallback(
    (key: string) => {
      const open = latest.current.drafts.find((d) => d.key === key);
      if (!open || open.saving) return;
      const handler = getDraftHandler(open.draft.kind);
      const readable = readAssistantDraft(open.draft);
      if (!handler || !readable) return;
      setDrafts((current) => current.map((d) => (d.key === key ? { ...d, saving: true, error: null } : d)));
      const epoch = scopeEpoch.current;
      const current = () => mounted.current && scopeEpoch.current === epoch;
      // A step the save has done (a board it created) goes back into the draft, so a retry after a later step fails
      // carries on from there instead of doing it again. Applied even while saving: it is the save's own write.
      const update = (payload: unknown) => {
        if (!current()) return;
        setDrafts((list) => list.map((d) => (d.key === key ? { ...d, draft: { ...d.draft, payload } } : d)));
      };
      // Once confirmed, the writes finish even if the coach closes the panel; only screen updates check `current`. A
      // handler that throws before its promise is caught the same way.
      Promise.resolve()
        .then(() => handler.confirm(readable, { navigate, update }))
        .then((result) => {
          if (!current()) return;
          setDrafts((list) => list.filter((d) => d.key !== key));
          setPrimary((shown) => (shown?.type === 'draft' && shown.key === key ? null : shown));
          setMessages((list) => keepNewest([...list, noteMessage(result.message, false, safePath(result.openPath), result.openLabel ?? null)]));
          setNotice(result.message);
        })
        .catch((err: unknown) => {
          if (!current()) return;
          const reason = err instanceof Error && err.message ? err.message : 'It could not be saved. Try again.';
          setDrafts((list) => list.map((d) => (d.key === key ? { ...d, saving: false, error: reason } : d)));
          setNotice(reason);
        });
    },
    [navigate]
  );

  const actionContext = useMemo<AssistantActionContext>(
    () => ({
      navigate: (path: string) => navigate(path),
      openDraft,
      say: (message: string) => void send(message),
      close: () => setIsOpen(false),
      notify: (message: string) => setNotice(message),
    }),
    [navigate, openDraft, send]
  );

  const runAction = useCallback(
    (action: AssistantAction, item?: unknown) => {
      if (!action.enabled) return;
      const handler = getAssistantAction(action.kind);
      if (!handler) {
        setNotice(`${action.label} is not available here yet.`);
        return;
      }
      void Promise.resolve(handler(action, actionContext, item)).catch(() => {
        if (mounted.current) setNotice(`${action.label} did not work. Try again.`);
      });
    },
    [actionContext]
  );

  const setScreenIds = useCallback((owner: string, ids: ScreenIds | null) => {
    if (ids && Object.values(ids).some((list) => list && list.length > 0)) screenIds.current.set(owner, ids);
    else screenIds.current.delete(owner);
  }, []);

  const open = useCallback((options?: { focus?: boolean }) => {
    setOpenFocus(options?.focus !== false);
    setIsOpen(true);
  }, []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => {
    setOpenFocus(true);
    setIsOpen((shown) => !shown);
  }, []);
  const showView = useCallback((key: string) => setPrimary({ type: 'view', key }), []);
  const showDraft = useCallback((key: string) => setPrimary({ type: 'draft', key }), []);
  const hidePrimary = useCallback(() => setPrimary(null), []);
  const startOver = useCallback(() => reset(false), [reset]);

  const value = useMemo<AssistantContextValue>(
    () => ({
      enabled,
      isOpen: enabled && isOpen,
      openFocus,
      open,
      close,
      toggle,
      messages,
      views,
      drafts,
      primary,
      showView,
      showDraft,
      hidePrimary,
      status,
      busy: status !== null,
      send,
      stop,
      reset: startOver,
      runAction,
      openDraft,
      updateDraft,
      discardDraft,
      confirmDraft,
      setScreenIds,
      notice,
    }),
    [enabled, isOpen, openFocus, open, close, toggle, messages, views, drafts, primary, showView, showDraft, hidePrimary, status, send, stop, startOver, runAction, openDraft, updateDraft, discardDraft, confirmDraft, setScreenIds, notice]
  );

  return <AssistantContext.Provider value={value}>{children}</AssistantContext.Provider>;
};
